import React, { useEffect, useRef, useState } from "react";
import { DayProps } from "react-day-picker";
import { format, isSameMonth } from "date-fns";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  formatPriceNoCurrency,
  formatCurrency,
  getStops,
  minutesTimeSpan,
  IItinerary,
} from "@thekilroygroup/shared-ui";
import { LocaleKey, localeMap } from "../../DatePickers/locales.interfaces";
import { DesktopSkeleton, MobileSkeleton } from "./OneWaySkeleton";
import { useTranslations } from "hooks/useTranslations";
import { useConfig } from "@thekilroygroup/shared-ui";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { sxStyles } from "../sharedStyles";
import { useCountUp } from "use-count-up";

interface ICalendarButtonOneWayProps extends DayProps {
  data: Record<string, IItinerary>;
  isFetching: boolean;
  setSelectedItinerary: (itinerary: IItinerary) => void;
  selectedItinerary?: IItinerary | null;
  lowestPrice: number | null;
  priceMemoryRef?: React.MutableRefObject<Record<string, number>>;
}
export const CalendarButtonOneWay = (props: ICalendarButtonOneWayProps) => {
  const {
    data,
    date,
    displayMonth,
    isFetching,
    setSelectedItinerary,
    selectedItinerary,
    lowestPrice,
  } = props;

  const isInDisplayMonth = isSameMonth(date, displayMonth);
  const formattedDate = format(date, "yyyy-MM-dd");
  const itinerary: IItinerary | null = data?.[
    formattedDate as keyof typeof data
  ] as IItinerary | null;
  const currency = itinerary?.totalFareAmounts?.currency || "";
  const departureDuration = itinerary?.flights?.[0]?.flightTime || 0;
  const departureStops =
    (itinerary?.flights?.[0]?.flightSegments?.length || 1) - 1;

  // Initialize from cross-mount memory when available to avoid starting from 0 on remounts
  const initialLocalPrice = props.priceMemoryRef?.current?.[formattedDate] ?? 0;
  const [localPrice, setLocalPrice] = useState(initialLocalPrice);

  const [price, setPrice] = useState(0);

  // Setup the animated counter first so it's available to following effects
  const { value: animatedPrice, reset: resetPrice } = useCountUp({
    isCounting: true,
    start: localPrice,
    end: price,
    duration: 0.4,
    onComplete: () => {
      setLocalPrice(price);
    },
  });

  const currentDisplayPriceRef = useRef(0);

  // Keep track of the currently displayed animated value
  useEffect(() => {
    currentDisplayPriceRef.current =
      Number(animatedPrice as unknown as number) || 0;
  }, [animatedPrice]);

  const didMountRef = useRef(false);

  useEffect(() => {
    const nextPrice = itinerary?.totalFareAmounts?.total || 0;
    setPrice(nextPrice);
    // Use the currently displayed value as the next animation start
    setLocalPrice(currentDisplayPriceRef.current || 0);
  }, [itinerary]);

  // Reset after state updates commit, so start uses latest localPrice
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    resetPrice();
  }, [localPrice, price]);

  // Persist latest completed price in memory for cross-mount continuity
  useEffect(() => {
    if (!itinerary || !props.priceMemoryRef) return;
    props.priceMemoryRef.current[formattedDate] = localPrice;
  }, [localPrice, itinerary]);

  // Check if this itinerary has the lowest price
  const isLowestPrice =
    price !== null && lowestPrice !== null && price === lowestPrice;

  const { breakpoints, spacing, shadows } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up("md"));
  const isMobile = useMediaQuery(breakpoints.down("sm"));

  const { locale } = useConfig();
  const { translations } = useTranslations();

  // Guard against undefined translations during language changes
  const stopsTranslations = translations?.searchResults?.stops || {};
  const timeLabels = translations?.timeLabels || {};

  return (
    <Button
      sx={{
        width: "100%",
        maxWidth: "initial",
        minWidth: "initial",
        padding: 0,
        color: "grey.800",
        display: isInDisplayMonth ? "block" : "none",
      }}
      variant="text"
      disabled={!itinerary}
      onClick={() => setSelectedItinerary(itinerary as IItinerary)}
    >
      <Stack
        sx={{
          alignItems: "left",
          textAlign: "left",
          padding: 1,
          justifyContent: "space-between",
          flexDirection: "column",
          border: "1px solid",
          borderColor: "white.main",
          width: "100%",
          maxWidth: "initial",
          borderRadius: 0,
          transition: "all 0.3s ease-in-out",
          height: { xs: spacing(8), sm: spacing(10), xl: spacing(12) },
          ...(selectedItinerary?.id === itinerary?.id &&
            selectedItinerary !== undefined && {
              border: "2px solid",
              borderColor: "primary.main",
            }),
        }}
      >
        {itinerary?.fareBreakdowns.some(
          (fare) => fare.passengerType === "YTH",
        ) ? (
          <InfoOutlinedIcon sx={sxStyles.infoIcon} />
        ) : itinerary?.fareBreakdowns.some(
            (fare) => fare.passengerType === "STU",
          ) ? (
          <InfoRoundedIcon sx={sxStyles.infoIcon} />
        ) : null}
        <Typography variant="titleExtraSmall">
          {format(date, isMobile ? "d" : "dd MMM", {
            locale: localeMap[locale as LocaleKey],
          })}
        </Typography>
        {itinerary || isFetching ? (
          <>
            {isDesktop ? (
              !isFetching || itinerary ? (
                <Stack direction="column" gap={0.5}>
                  <Stack direction="row" alignItems="baseline" gap={0.5}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: isLowestPrice ? "success.main" : "inherit",
                      }}
                    >
                      {formatPriceNoCurrency(
                        Number(animatedPrice as unknown as number) || 0,
                        locale,
                      )}
                    </Typography>
                    <Typography
                      variant="titleMedium"
                      weight="bold"
                      sx={{
                        color: isLowestPrice ? "success.main" : "inherit",
                      }}
                    >
                      {formatCurrency(currency, locale)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <FlightTakeoffIcon sx={{ fontSize: "14px" }} />
                    <Typography variant="titleExtraSmall" fontWeight="400">
                      {minutesTimeSpan(departureDuration, timeLabels)}
                    </Typography>
                    <Typography
                      variant="titleExtraSmall"
                      fontWeight="400"
                      sx={{ flex: "1", textAlign: "end" }}
                    >
                      {getStops(departureStops, stopsTranslations)}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <DesktopSkeleton />
              )
            ) : isFetching ? (
              <MobileSkeleton />
            ) : (
              <>
                <Typography
                  variant={isMobile ? "titleExtraSmall" : "titleMedium"}
                  weight="bold"
                  sx={{
                    mt: "auto",
                    color: isLowestPrice ? "success.main" : "inherit",
                    fontWeight: isLowestPrice ? "bold" : "inherit",
                  }}
                >
                  {formatCurrency(currency, locale)}
                  <br />
                  {formatPriceNoCurrency(
                    Number(animatedPrice as unknown as number) || 0,
                    locale,
                  )}
                </Typography>
                {!isMobile && (
                  <Typography variant="titleExtraSmall" fontWeight="400">
                    {getStops(departureStops, stopsTranslations)}
                  </Typography>
                )}
              </>
            )}
          </>
        ) : (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            -
          </Box>
        )}
      </Stack>
    </Button>
  );
};
